import { Box, Paper, Skeleton, Typography } from '@mui/material';
import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { PALETTE } from 'entities/analytics/lib';
import { useRegistrationFunnel } from 'entities/analytics/model';
import { EChart } from 'entities/analytics/ui/echart';

import { EUserOnboardingStage, SourceCombination, VaccinationSource } from 'shared/api';

// Локальные лейблы стадий — те же формулировки, что на странице юзеров (STAGE_META),
// но без кросс-импорта entities/user → entities/analytics
const STAGE_LABELS: Record<EUserOnboardingStage, string> = {
  [EUserOnboardingStage.CodeNotInserted]: 'Code not inserted',
  [EUserOnboardingStage.NoOwnerProfile]: 'No profile',
  [EUserOnboardingStage.NoVaccinations]: 'No vaccinations',
  [EUserOnboardingStage.OnboardingCompleted]: 'Completed',
};

const STAGE_COLORS: Record<EUserOnboardingStage, string> = {
  [EUserOnboardingStage.CodeNotInserted]: PALETTE.muted,
  [EUserOnboardingStage.NoOwnerProfile]: PALETTE.amber,
  [EUserOnboardingStage.NoVaccinations]: PALETTE.blue,
  [EUserOnboardingStage.OnboardingCompleted]: PALETTE.primary,
};

const SOURCE_LABELS: Record<VaccinationSource, string> = {
  ownerPeople: 'Owner',
  otherPeople: 'Other people',
  pets: 'Pets',
};

// Канонический порядок источников внутри комбинации — для стабильных лейблов и цветов
const SOURCE_ORDER: VaccinationSource[] = ['ownerPeople', 'otherPeople', 'pets'];

const isKnownSource = (s: string): s is VaccinationSource => (SOURCE_ORDER as string[]).includes(s);

// Неизвестный источник (бек типизирует sources как string[]): camelCase → 'Future source'
const humanizeSource = (s: string): string => {
  const spaced = s.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Детерминированный fallback-цвет по ключу комбинации: разные неизвестные
// комбинации не должны сливаться в один серый
const FALLBACK_COLORS = [PALETTE.axis, '#b0895e', '#7a9e9f', '#a3719c'];

const fallbackColor = (key: string): string => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
};

// Цвет привязан к нормализованному набору источников, не к индексу:
// бек сортирует combinations по убыванию count, порядок не канонический
const COMBO_COLORS: Record<string, string> = {
  ownerPeople: PALETTE.primary,
  otherPeople: PALETTE.blue,
  pets: PALETTE.amber,
  'ownerPeople+otherPeople': PALETTE.primaryDark,
  'ownerPeople+pets': '#8e7cc3',
  'otherPeople+pets': PALETTE.danger,
  'ownerPeople+otherPeople+pets': '#5c6f6a',
};

const normalizeSources = (sources: string[]): string[] =>
  [...sources].sort((a, b) => {
    const ai = (SOURCE_ORDER as string[]).indexOf(a);
    const bi = (SOURCE_ORDER as string[]).indexOf(b);
    // Неизвестные источники (бек типизирует sources как string[]) — в конец, по алфавиту
    return (ai === -1 ? SOURCE_ORDER.length : ai) - (bi === -1 ? SOURCE_ORDER.length : bi) || a.localeCompare(b);
  });

const comboLabel = (combo: SourceCombination): string =>
  normalizeSources(combo.sources)
    .map((s) => (isKnownSource(s) ? SOURCE_LABELS[s] : humanizeSource(s)))
    .join(' + ');

const comboColor = (combo: SourceCombination): string => {
  const key = normalizeSources(combo.sources).join('+');
  return COMBO_COLORS[key] ?? fallbackColor(key);
};

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SourceStat = ({ label, value }: { label: string; value: number }) => (
  <Box>
    <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
      {value.toLocaleString()}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const CenteredNote = ({ children, height }: { children: string; height: number }) => (
  <Box height={height} display="flex" alignItems="center" justifyContent="center" color="text.secondary">
    <Typography variant="body2">{children}</Typography>
  </Box>
);

export const RegistrationFunnelCard = () => {
  const { data, isLoading, isFetching, isError } = useRegistrationFunnel();
  const d = data?.data;

  const stages = useMemo(() => d?.funnel ?? [], [d]);
  const combinations = useMemo(() => d?.completedBySource.combinations ?? [], [d]);
  const completed = stages.find((s) => s.stage === EUserOnboardingStage.OnboardingCompleted);

  const barsOption = useMemo<EChartsOption>(
    () => ({
      grid: { left: 14, right: 90, top: 8, bottom: 8, containLabel: true },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        splitLine: { lineStyle: { color: PALETTE.grid } },
        axisLabel: { color: PALETTE.axis },
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: stages.map((s) => STAGE_LABELS[s.stage] ?? s.stage),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#dfe5e3' } },
        axisLabel: { color: '#40514d', fontSize: 12 },
      },
      series: [
        {
          type: 'bar',
          data: stages.map((s) => ({ value: s.count, itemStyle: { color: STAGE_COLORS[s.stage] ?? PALETTE.axis } })),
          barWidth: 18,
          itemStyle: { borderRadius: [0, 4, 4, 0] },
          label: {
            show: true,
            position: 'right',
            color: PALETTE.axis,
            fontSize: 11,
            formatter: (p: { dataIndex?: number }) => {
              const stage = stages[p.dataIndex ?? 0];
              return stage ? `${stage.count.toLocaleString()} · ${stage.percent.toFixed(1)}%` : '';
            },
          },
        },
      ],
    }),
    [stages],
  );

  const donutOption = useMemo<EChartsOption>(
    () => ({
      tooltip: { trigger: 'item', valueFormatter: (v) => Number(v ?? 0).toLocaleString() },
      // До 7 длинных лейблов в полукарточке 200px высотой — без scroll легенда наезжает на донат
      legend: {
        type: 'scroll',
        bottom: 0,
        left: 'center',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: '#40514d', fontSize: 11 },
      },
      series: [
        {
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['50%', '42%'],
          label: { show: false },
          data: combinations.map((c) => ({
            name: comboLabel(c),
            value: c.count,
            itemStyle: { color: comboColor(c) },
          })),
        },
      ],
    }),
    [combinations],
  );

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={500}>
        Registration funnel
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Mutually exclusive onboarding stages — population shares, not step-to-step conversion. Right side: completed
        accounts by vaccination source.
      </Typography>

      {isLoading ? (
        <Skeleton variant="rectangular" height={300} sx={{ mt: 1.5, borderRadius: 1 }} />
      ) : isError && !d ? (
        // Полноценная ошибка — только когда показывать нечего; при живом кэше снимок остаётся видимым
        <CenteredNote height={120}>Failed to load registration funnel</CenteredNote>
      ) : d && d.calculatedAt === null ? (
        <CenteredNote height={120}>Snapshot not calculated yet</CenteredNote>
      ) : d ? (
        <Box sx={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {isError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              Refresh failed — showing the last loaded snapshot
            </Typography>
          )}
          <Box display="flex" gap={4} mt={1.5} flexWrap="wrap">
            <Box>
              <Typography variant="h5" fontWeight={500} lineHeight={1.2}>
                {d.total.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                accounts in snapshot
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={500} lineHeight={1.2}>
                {(completed?.count ?? 0).toLocaleString()} · {(completed?.percent ?? 0).toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                completed onboarding
              </Typography>
            </Box>
          </Box>

          {/* minWidth: 0 на панелях: иначе grid-трек не ужимается ниже min-content канваса
              и карточка распирает страницу по горизонтали */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, mt: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <EChart option={barsOption} height={260} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              {combinations.length > 0 ? (
                <EChart option={donutOption} height={200} />
              ) : (
                <CenteredNote height={200}>No completed accounts</CenteredNote>
              )}
              <Box sx={{ display: 'flex', gap: 4, mt: 1, flexWrap: 'wrap' }}>
                <SourceStat label="Owner" value={d.completedBySource.ownerPeople} />
                <SourceStat label="Other people" value={d.completedBySource.otherPeople} />
                <SourceStat label="Pets" value={d.completedBySource.pets} />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Overlapping totals — one account can count in several sources
              </Typography>
            </Box>
          </Box>

          {d.calculatedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Snapshot calculated at {formatDateTime(d.calculatedAt)}
            </Typography>
          )}
        </Box>
      ) : null}
    </Paper>
  );
};
